import { useRef, useState } from "react";
import * as XLSX from "xlsx";
import { useSession } from "@/auth/session";
import { bulkImportReadings, type BulkImportReadingRow, type BulkImportReadingsResult } from "@/lib/api";

type ParsedRow = {
  line: number;
  contractNumber: string;
  periodCode: string;
  previousReading: number;
  currentReading: number;
  consumption: number;
  readAt: string;
  rawPeriod: string;
  rawReadAt: string;
};

export default function ImportarLecturasPage() {
  const { session } = useSession();
  const fileRef = useRef<HTMLInputElement>(null);
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [result, setResult] = useState<BulkImportReadingsResult | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [previewPage, setPreviewPage] = useState(1);

  const PAGE_SIZE = 20;
  const totalPages = Math.ceil(rows.length / PAGE_SIZE);
  const pagedRows = rows.slice((previewPage - 1) * PAGE_SIZE, previewPage * PAGE_SIZE);

  const detectedPeriod = rows.length > 0 ? rows[0].periodCode : null;
  const uniquePeriods = [...new Set(rows.map((r) => r.periodCode))];

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setParseError(null);
    setResult(null);
    setImportError(null);
    setRows([]);
    setPreviewPage(1);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: "array" });

        const sheetName = workbook.SheetNames.find((n) => n.toLowerCase().includes("lectura")) ?? workbook.SheetNames[0];
        if (!sheetName) throw new Error("No se encontró hoja de lecturas en el archivo");

        const ws = workbook.Sheets[sheetName];
        const raw = XLSX.utils.sheet_to_json<Record<string, unknown>>(ws, { defval: "" });

        const parsed: ParsedRow[] = [];
        for (let i = 0; i < raw.length; i++) {
          const row = raw[i];
          const contractRaw = String(row["contratp"] ?? "").trim().replace(/\.0+$/, "");
          const periodRaw = row["periodo"];
          const prevRaw = row["lectura anterior"];
          const currRaw = row["lectura actual"];
          const fechaRaw = String(row["fecha_lectura"] ?? "").trim();

          if (!contractRaw || periodRaw == null || periodRaw === "") continue;

          const prev = Number(prevRaw ?? 0);
          const curr = Number(currRaw ?? 0);
          if (isNaN(prev) || isNaN(curr)) continue;

          const periodCode = parsePeriodCode(periodRaw);
          const readAt = parseReadAt(fechaRaw);

          parsed.push({
            line: i + 2,
            contractNumber: contractRaw,
            periodCode,
            previousReading: prev,
            currentReading: curr,
            consumption: Math.max(0, curr - prev),
            readAt,
            rawPeriod: String(periodRaw),
            rawReadAt: fechaRaw,
          });
        }

        if (parsed.length === 0) throw new Error("No se encontraron filas válidas en la hoja Lecturas");
        setRows(parsed);
      } catch (err) {
        setParseError(err instanceof Error ? err.message : "No se pudo leer el archivo");
      }
    };
    reader.readAsArrayBuffer(file);
  }

  async function handleImport() {
    if (!session || rows.length === 0) return;
    setIsImporting(true);
    setImportError(null);
    setResult(null);

    try {
      const payload: BulkImportReadingRow[] = rows.map((r) => ({
        contractNumber: r.contractNumber,
        periodCode: r.periodCode,
        previousReading: r.previousReading,
        currentReading: r.currentReading,
        readAt: r.readAt,
      }));
      const res = await bulkImportReadings(session, payload);
      setResult(res);
    } catch (err) {
      setImportError(err instanceof Error ? err.message : "Error al importar");
    } finally {
      setIsImporting(false);
    }
  }

  function handleReset() {
    setRows([]);
    setResult(null);
    setParseError(null);
    setImportError(null);
    setPreviewPage(1);
    if (fileRef.current) fileRef.current.value = "";
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-gray-900">Importar Lecturas</h1>
        <p className="text-sm text-gray-500 mt-1">
          Carga masiva de lecturas desde Excel. Crea nuevas lecturas o actualiza las existentes para el periodo indicado.
        </p>
      </div>

      {/* Upload */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
        <h2 className="text-sm font-semibold text-gray-700">1. Seleccionar archivo</h2>
        <p className="text-xs text-gray-500">
          Formato esperado: Excel (.xlsx) con hoja <strong>Lecturas</strong> con columnas{" "}
          <code className="bg-gray-100 px-1 rounded">contratp | periodo | lectura anterior | lectura actual | consumo | fecha_lectura</code>
        </p>
        <div className="flex items-center gap-3">
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileChange}
            className="text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
          {rows.length > 0 && (
            <button onClick={handleReset} className="text-xs text-gray-400 hover:text-gray-600 underline">
              Limpiar
            </button>
          )}
        </div>
        {parseError && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-2">{parseError}</div>
        )}
      </div>

      {/* Preview */}
      {rows.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-700">2. Vista previa</h2>
            <div className="flex items-center gap-4 text-xs text-gray-500">
              <span>{rows.length} filas detectadas</span>
              {uniquePeriods.map((p) => (
                <span key={p} className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                  Periodo: {p}
                </span>
              ))}
            </div>
          </div>

          <div className="overflow-x-auto rounded-lg border border-gray-100">
            <table className="min-w-full text-xs">
              <thead className="bg-gray-50">
                <tr>
                  {["Fila", "Contrato", "Periodo", "Lect. Anterior", "Lect. Actual", "Consumo", "Fecha Lectura"].map((h) => (
                    <th key={h} className="px-3 py-2 text-left font-medium text-gray-500">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pagedRows.map((row) => (
                  <tr key={row.line} className="hover:bg-gray-50">
                    <td className="px-3 py-1.5 text-gray-400">{row.line}</td>
                    <td className="px-3 py-1.5 font-mono font-medium text-gray-800">{row.contractNumber}</td>
                    <td className="px-3 py-1.5 text-gray-600">{row.periodCode}</td>
                    <td className="px-3 py-1.5 text-right text-gray-600">{row.previousReading}</td>
                    <td className="px-3 py-1.5 text-right text-gray-600">{row.currentReading}</td>
                    <td className="px-3 py-1.5 text-right font-medium text-blue-700">{row.consumption}</td>
                    <td className="px-3 py-1.5 text-gray-500">{row.readAt}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-gray-100 px-3 py-2">
                <span className="text-xs text-gray-400">
                  Mostrando {(previewPage - 1) * PAGE_SIZE + 1}–{Math.min(previewPage * PAGE_SIZE, rows.length)} de {rows.length} filas
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPreviewPage(1)}
                    disabled={previewPage === 1}
                    className="px-2 py-1 text-xs rounded text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    «
                  </button>
                  <button
                    onClick={() => setPreviewPage((p) => p - 1)}
                    disabled={previewPage === 1}
                    className="px-2 py-1 text-xs rounded text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    ‹
                  </button>
                  {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                    const page = totalPages <= 7 ? i + 1 : previewPage <= 4 ? i + 1 : previewPage >= totalPages - 3 ? totalPages - 6 + i : previewPage - 3 + i;
                    return (
                      <button
                        key={page}
                        onClick={() => setPreviewPage(page)}
                        className={`px-2.5 py-1 text-xs rounded ${page === previewPage ? "bg-blue-600 text-white font-medium" : "text-gray-500 hover:bg-gray-100"}`}
                      >
                        {page}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setPreviewPage((p) => p + 1)}
                    disabled={previewPage === totalPages}
                    className="px-2 py-1 text-xs rounded text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    ›
                  </button>
                  <button
                    onClick={() => setPreviewPage(totalPages)}
                    disabled={previewPage === totalPages}
                    className="px-2 py-1 text-xs rounded text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    »
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Import button */}
          <div className="flex items-center gap-3 pt-2">
            <button
              onClick={handleImport}
              disabled={isImporting || !session}
              className="px-5 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isImporting ? "Importando..." : `Importar ${rows.length} lecturas`}
            </button>
            {detectedPeriod && (
              <span className="text-xs text-gray-500">
                Se importarán al periodo <strong>{detectedPeriod}</strong>. Si la lectura ya existe, se actualizará.
              </span>
            )}
          </div>
        </div>
      )}

      {/* Result */}
      {importError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-5">
          <p className="text-sm font-medium text-red-700">Error al importar</p>
          <p className="text-sm text-red-600 mt-1">{importError}</p>
        </div>
      )}

      {result && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
          <h2 className="text-sm font-semibold text-gray-700">3. Resultado</h2>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-green-700">{result.imported}</div>
              <div className="text-xs text-green-600 mt-1">Creadas</div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-blue-700">{result.updated}</div>
              <div className="text-xs text-blue-600 mt-1">Actualizadas</div>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-gray-700">{result.skipped}</div>
              <div className="text-xs text-gray-500 mt-1">Omitidas</div>
            </div>
          </div>
          {result.errors.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs font-medium text-gray-600">Errores ({result.errors.length}):</p>
              <div className="max-h-48 overflow-y-auto bg-gray-50 rounded-lg p-3 space-y-0.5">
                {result.errors.map((err, idx) => (
                  <div key={idx} className="text-xs text-red-600">
                    {err}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function parsePeriodCode(raw: unknown): string {
  // Excel stores dates as serial numbers (e.g. 46174 = 01-06-2026)
  if (typeof raw === "number") {
    const d = XLSX.SSF.parse_date_code(raw);
    return `${d.y}-${String(d.m).padStart(2, "0")}`;
  }
  // "01-06-2026" (DD-MM-YYYY) → "2026-06"
  const str = String(raw ?? "").trim();
  const parts = str.split(/[-/]/);
  if (parts.length >= 3) {
    const yearPart = parts.find((p) => p.length === 4) ?? parts[2];
    const month = parts[1]?.padStart(2, "0") ?? "01";
    return `${yearPart}-${month}`;
  }
  return str;
}

function parseReadAt(raw: string): string {
  if (!raw) return new Date().toISOString().slice(0, 10);
  // "05-04-2026 / 05-05-26" → take second date (end of reading period)
  const parts = raw.split("/");
  const datePart = (parts[1] ?? parts[0]).trim();
  return normalizeDateStr(datePart);
}

function normalizeDateStr(raw: string): string {
  const parts = raw.split(/[-/]/).map((p) => p.trim());
  if (parts.length < 3) return new Date().toISOString().slice(0, 10);
  let [d, m, y] = parts;
  if (y.length === 2) y = `20${y}`;
  if (y.length === 4 && d.length <= 2 && m.length <= 2) {
    return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }
  // already YYYY-MM-DD
  return `${d}-${m.padStart(2, "0")}-${y.padStart(2, "0")}`;
}
